
import { useEffect, useRef, useState } from 'react';

export default function Challenges() {
  const [challengesVisible, setChallengesVisible] = useState(false);
  const challengesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setChallengesVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (challengesRef.current) observer.observe(challengesRef.current);

    return () => observer.disconnect();
  }, []);

  const challenges = [
    { icon: 'ri-traffic-light-line', text: 'ازدحامات الطرق المرورية', percent: 42 },
    { icon: 'ri-parking-box-line', text: 'صعوبة إيجاد المواقف', percent: 28 },
    { icon: 'ri-time-line', text: 'ضيق الوقت وطول الانتظار', percent: 18 },
    { icon: 'ri-map-pin-range-line', text: 'بُعد المسافة عن العيادات', percent: 12 },
  ];

  return (
    <section ref={challengesRef} className="py-24 bg-white">
      <div className="container mx-auto px-4" style={{ maxWidth: '1140px' }}>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image Side */}
          <div
            className={`relative transition-all duration-1000 ${
              challengesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="https://public.readdy.ai/ai/img_res/71265e5cf1dae2416a53fd829356e7e1.jpg"
                alt="ازدحامات الطرق وصعوبة المواقف"
                className="w-full h-[550px] object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
            </div>
          </div>

          {/* Content Side */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              challengesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="inline-block bg-red-50 px-5 py-2 rounded-full mb-4">
              <span className="text-red-600 font-semibold text-sm">نتفهم التحديات</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              نوفّر عليك عناء الطريق
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              استناداً إلى استطلاع أجريناه مع عملائنا، تبيّن أن <strong>ازدحامات الطرق</strong> و<strong>صعوبة إيجاد المواقف</strong> وضيق الوقت وبُعد المسافة تمثّل العوائق الرئيسية التي تحول دون زيارة العيادات. لذلك، قرّرنا في <strong>سماعتي</strong> أن نُلغي هذه العوائق كلياً — فأخصائيونا يصلون إليك أينما كنت، بكامل المعدات والكفاءة المهنية.
            </p>

            {/* Survey Results */}
            <div className="bg-gray-50 rounded-2xl p-6 mb-8">
              <h4 className="text-base font-bold text-gray-800 mb-5 flex items-center gap-2">
                <i className="ri-bar-chart-2-line text-[#184187]"></i>
                أبرز أسباب استدعاء عملائنا لنا
              </h4>
              <div className="space-y-4">
                {challenges.map((item, idx) => (
                  <div
                    key={idx}
                    className={`transition-all duration-700 ${
                      challengesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                    }`}
                    style={{ transitionDelay: `${400 + idx * 150}ms` }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#184187]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-[#184187] text-base`}></i>
                        </div>
                        <span className="text-sm font-medium text-gray-700">{item.text}</span>
                      </div>
                      <span className="text-sm font-bold text-[#184187]">{item.percent}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="bg-[#184187] h-2.5 rounded-full transition-all duration-1500 ease-out"
                        style={{
                          width: challengesVisible ? `${item.percent}%` : '0%',
                          transitionDelay: `${600 + idx * 150}ms`,
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4 bg-[#184187]/5 rounded-xl p-5 border border-[#184187]/10">
              <div className="w-14 h-14 bg-[#184187] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-home-smile-2-line text-2xl text-white"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">الحل مع سماعتي</h4>
                <p className="text-sm text-gray-600">
                  لا حاجة للخروج من المنزل — الأخصائي يأتي إليك بكامل المعدات والأجهزة اللازمة
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
